import { useId, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import clsx from "clsx";
import { X, Globe } from "lucide-react";
import { domainOf, parseAddressList } from "@dfws/contracts";
import { useChatPeople } from "../chat/chatData";
import s from "./Compose.module.css";

type Addr = { name: string | null; email: string };

/**
 * Recipients as chips. Typing suggests colleagues; Enter, Tab or a comma adds; pasting a list adds them all;
 * Backspace on an empty input removes the last. People outside your organisation are marked on their chip.
 */
export function RecipientField({ label, value, onChange, myDomain, autoFocus }: { label: string; value: Addr[]; onChange: (v: Addr[]) => void; myDomain: string; autoFocus?: boolean }) {
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const people = useChatPeople();
  const q = text.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!q) return [];
    const taken = new Set(value.map((a) => a.email.toLowerCase()));
    return (people.data?.items ?? []).filter((p) => !taken.has(p.email) && (p.name.toLowerCase().includes(q) || p.email.includes(q))).slice(0, 6);
  }, [q, people.data, value]);

  const add = (list: Addr[]) => {
    const seen = new Set(value.map((a) => a.email.toLowerCase()));
    const next = [...value];
    for (const a of list) {
      if (seen.has(a.email.toLowerCase())) continue;
      seen.add(a.email.toLowerCase());
      next.push(a);
    }
    onChange(next);
    setText("");
    setActive(0);
  };
  const commit = () => {
    const pick = suggestions[active];
    if (pick && q) return add([{ name: pick.name, email: pick.email }]);
    const parsed = parseAddressList(text);
    if (parsed.length) add(parsed);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === "Tab" || e.key === "," || e.key === ";") && text.trim()) {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !text && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const parsed = parseAddressList(e.clipboardData.getData("text"));
    if (parsed.length > 1 || (parsed.length === 1 && !text)) {
      e.preventDefault();
      add(parsed);
    }
  };

  return (
    <div className={s.field} onClick={() => input.current?.focus()}>
      <span className={s.fieldLabel}>{label}</span>
      <div className={s.chips}>
        {value.map((a) => {
          const external = myDomain && domainOf(a.email) !== myDomain.toLowerCase();
          return (
            <span key={a.email} className={clsx(s.chip, external && s.chipExternal)} title={a.name ? `${a.name} <${a.email}>` : a.email}>
              {external && <Globe aria-label="Outside your organisation" className={s.chipIcon} />}
              <span className={s.chipText}>{a.name || a.email}</span>
              <button type="button" className={s.chipRemove} aria-label={`Remove ${a.email}`} onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((x) => x.email !== a.email));
                }}>
                <X />
              </button>
            </span>
          );
        })}
        <input
          ref={input}
          className={s.chipInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => text.trim() && commit()}
          aria-label={label}
          aria-autocomplete="list"
          aria-controls={suggestions.length ? listId : undefined}
          aria-expanded={suggestions.length > 0}
          role="combobox"
          autoFocus={autoFocus}
          inputMode="email"
          autoComplete="off"
        />
      </div>
      {suggestions.length > 0 && (
        <ul id={listId} role="listbox" className={s.suggest} aria-label={`${label} suggestions`}>
          {suggestions.map((p, i) => (
            <li key={p.email} role="option" aria-selected={i === active} className={clsx(s.suggestItem, i === active && s.suggestActive)} onMouseDown={(e) => {
                e.preventDefault();
                add([{ name: p.name, email: p.email }]);
              }}>
              <span className={s.suggestName}>{p.name}</span>
              <span className={s.suggestEmail}>{p.email}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
